import { OrderItemDelivery } from '../models/order-item-delivery.model';
import { Order } from '../models/order.model';
import { User } from '../models/user.model';
import { OrderTypes } from '../enums/order-types';
import { OrderItem } from '../models/order-item.model';
import { Address } from '../models/address.model';
import { floorLastCent, isEmptyArray } from './utils';
import { CAROUSEL_TYPE } from '../constants/product-type';
import { ShippingMethod } from '../enums/shipping-method';
import { ProductCatalog } from '../models/product-catalog.model';
import { HeroSlide } from '../interfaces/hero-slide';
import { WslrAddress } from '../interfaces/wslr-address';
import { FilterGroup } from '../models/filter-group.model';
import { FilterItemLabel } from '../enums/filter-item-label';

export function computeShipmentLabel(
  delivery: OrderItemDelivery,
  attention: string
): string {
  if (delivery.deliver_to) {
    return delivery.deliver_to;
  }

  // IMS-BTI label has higher priority
  if (delivery.isIMS) {
    return delivery.IMSAttention;
  }

  if (delivery.isWSLR) {
    return delivery.wslrLabel + (attention ? ` - ${attention}` : '');
  }

  if (attention) {
    return attention;
  }

  return delivery.label || '';
}

export function isWSLROrder(order: Order, currentUser: User) {
  if (!order || !currentUser) {
    return false;
  }
  const result =
    order.entity_id > 0 && order.entity_id !== currentUser.entity_id;
  return result;
}

export function isPaymentMethodDefined(
  order: Order,
  currentUser: User,
  canEditSap = false
) {
  if (!order) {
    return false;
  }

  if (!canEditSap) {
    return true;
  }

  if (isWSLROrder(order, currentUser)) {
    return true;
  }

  if (order.type === OrderTypes.ECOMMERCE_TYPE) {
    return order.hasCostCenter() || order.hasCreditCard();
  } else {
    return order.hasCostCenter();
  }
}

export function computeAvailableAddresses(
  userAddresses: Address[],
  order: Order,
  orderItem: OrderItem,
  includeDelivery: OrderItemDelivery = null,
  skipExistingDeliveriesCheck: boolean = false,   //   when true it will not exclude already selected deliveries
): Address[] {
  if (!order) {
    return [];
  }

  const hasVariations = orderItem?.product?.hasVariations;
  const shipmentMethods =
    orderItem && orderItem.product ? orderItem.product.shipping_options : {};
  const isInternationalAllowed =
    shipmentMethods[ShippingMethod.International] !== -1;

  const autoshipAddresses: Address[] = order.isCustom
    ? order.autoshipLocations.map((l) => Address.fromEntityLocation(l))
    : [];

  let allAddresses = [];
  if (order.isWSLR) {
    if (order.isCustom) {
      // // only first available address
      // allAddresses =  !isEmptyArray(autoshipAddresses) ? [autoshipAddresses[0]] : [];
      // show all addresses
      allAddresses = [...autoshipAddresses];
    } else {

      const entityAddress = Address.fromEntityLocation(
        order.entity.getDefaultShippingLocation()
      );

      if (entityAddress) {
        allAddresses = [entityAddress];
      } else {
        allAddresses = [...autoshipAddresses];
      }
    }
  } else {
    allAddresses = [...userAddresses, ...autoshipAddresses];
  }

  const selectedAddresses =
    orderItem && !isEmptyArray(orderItem.deliveries) && !skipExistingDeliveriesCheck
      ? orderItem.deliveries
          .filter((d) => {
            if (!d.addr_id) {
              return false;
            }
            if (!includeDelivery) {
              return true;
            }

            return d.uuid !== includeDelivery.uuid;
          })
          .map((d) => d.getAddress().longAddress)
      : [];

  const filtered = allAddresses.filter((a) => {
    if (!isInternationalAllowed && a.isInternational) {
      return false; // do not search international address if they are not allowed
    }

    if (!a.is_valid) {
      return false;
    }

    if (hasVariations) {
      // for products with variations returns all addresses
      return true;
    }

    return !selectedAddresses.includes(a.longAddress);
  });

  return filtered;
}

export function findDetailedErrorMessage(
  error: any = {},
  options: { hideResponseError?: boolean; customErrorText?: string } = {}
): string {
  if (options.hideResponseError) {
    return '';
  }

  if (options.customErrorText) {
    return options.customErrorText;
  }

  const errorData = error.error;
  if (errorData) {
    if (errorData.data) {
      if (typeof errorData.data === 'string') {
        return errorData.data;
      }
      if (!isEmptyArray(errorData.data.messages)) {
        return errorData.data.messages.join('<br/>');
      }
      if (!isEmptyArray(errorData.data)) {
        return errorData.data.join(' ');
      }
    } else if (errorData.message) {
      return errorData.message;
    } else {
      return 'There was a problem connecting to the server. Please reload the page and try again.';
    }
  }
  return '';
}

export function computeDiscountPrice(price: number, discount: number): number {
  if (!price) {
    return 0;
  }

  if (!discount) {
    discount = 0;
  }

  return floorLastCent(price * (1 - discount / 100));
}

export function computeProductPrice(product, quantity = 1, sku = null): number {
  if (!product) {
    return 0;
  }

  return product.getPrice(quantity, sku);
}

export function computeDiscountProductPrice(
  product,
  quantity = 1,
  sku = null
): number {
  if (!product) {
    return 0;
  }
  const price = computeProductPrice(product, quantity, sku);

  if (!product.discount) {
    return price;
  }

  return computeDiscountPrice(price, this.discount);
}

export function computeMinQuantity(product, sku = null): number {
  if (!product) {
    return 0;
  }
  return product.getMinQuantity(sku);
}

export function computeGroupMinQuantity(product, sku = null): number {
  if (!product) {
    return 0;
  }
  return product.getGroupMinQuantity(sku);
}

export function computeMaxQuantity(product, sku = null): number {
  if (!product) {
    return 0;
  }
  return product.getMaxQuantity(sku);
}

export function computeAggregatedQuantity(product, sku = null): number {
  if (!product) {
    return 0;
  }
  return product.getAggregatedQuantity(sku);
}

export function computeGroupAggregatedQuantity(product, sku = null): number {
  if (!product) {
    return 0;
  }
  return product.getGroupAggregatedQuantity(sku);
}

export function convertCatalogsToHeroSlides(
  catalogs: ProductCatalog[]
): HeroSlide[] {
  const result: HeroSlide[] = [];
  if (isEmptyArray(catalogs)) {
    return result;
  }
  catalogs.forEach((catalog) => {
    if (catalog.featureType === CAROUSEL_TYPE) {
      const img = catalog.getHeroImage();
      if (img) {
        result.push({
          image: img['url'],
          headline: catalog.label,
          description: '',
          url: catalog.programDetailsUrl,
        });
      }
    }
  });

  return result.slice(0, 5);
}

export function getSortedAddressesByWslr(
  addresses: Address[],
  userEntityId: number
): WslrAddress[] {
  if (isEmptyArray(addresses)) {
    return [];
  }

  const findDefaultForWslr = (
    wslr: number,
    addressList: Address[]
  ): Address[] => {
    return findAddressByWslr(wslr, addressList).filter((a) => a.is_default);
  };

  const findNonDefaultForWslr = (
    wslr: number,
    addressList: Address[]
  ): Address[] => {
    return findAddressByWslr(wslr, addressList).filter((a) => !a.is_default);
  };

  const findAddressByWslr = (
    wslr: number,
    addressList: Address[]
  ): Address[] => {
    if (isEmptyArray(addressList)) {
      return [];
    }
    return addressList.filter((a) => !a.entity_id || a.entity_id === wslr);
  };

  const wslrIds = getSortedWslrIds(addresses, userEntityId);
  const sortedAddresses: WslrAddress[] = [];

  wslrIds.forEach((id) => {
    const defaultAddresses = findDefaultForWslr(id, addresses);
    const nonDefaultAddresses = findNonDefaultForWslr(id, addresses);
    sortedAddresses.push({
      wslrId: id,
      default: defaultAddresses,
      addresses: nonDefaultAddresses,
    });
  });

  return sortedAddresses;
}

export function flatternSortedAddressesByWslr(
  addressList: Address[],
  userEntityId: number
) {
  const sortedAddresses = getSortedAddressesByWslr(addressList, userEntityId);
  // find all default;
  const defaults = [];
  const nonDefaults = [];

  const alreadyExists = (output: Address[], input: Address): boolean => {
    return output.some((a) => a.id === input.id);
  };

  sortedAddresses.forEach((wslrAddress) => {
    if (!isEmptyArray(wslrAddress.default)) {
      wslrAddress.default.forEach((a) => {
        const addressAdded =
          alreadyExists(defaults, a) || alreadyExists(nonDefaults, a);
        if (!addressAdded) {
          defaults.push(a);
        }
      });
    }

    if (!isEmptyArray(wslrAddress.addresses)) {
      wslrAddress.addresses.forEach((a) => {
        const addressAdded =
          alreadyExists(defaults, a) || alreadyExists(nonDefaults, a);
        if (!addressAdded) {
          nonDefaults.push(a);
        }
      });
    }
  });

  return [...defaults, ...nonDefaults];
  // remove duplicates
  // return joinedAddresses.filter( (value, index, self) => {
  //     return index  === self.findIndex( v => v.id === value.id);
  // });
}

export function getSortedWslrIds(
  addressesList: Address[],
  userEntityId: number
): number[] {
  if (isEmptyArray(addressesList)) {
    return [];
  }
  let ids: number[] = [];

  addressesList.forEach((a) => {
    const idList = a.wslrIds;

    idList.forEach((id) => {
      const addressEntityId = id || userEntityId;
      if (!ids.includes(addressEntityId)) {
        ids.push(addressEntityId);
      }
    });
  });

  if (ids.length > 1) {
    if (ids.includes(userEntityId)) {
      const restIds = ids.filter((i) => i !== userEntityId).sort();
      ids = [];
      ids.push(userEntityId, ...restIds);
    } else {
      ids = ids.sort();
    }
  }
  return ids;
}

export function getFilterMinMaxPrices(filterGroups: FilterGroup[]): {
  minPrice: number;
  maxPrice: number;
} {
  if (isEmptyArray(filterGroups)) {
    return null;
  }

  const priceFilter = filterGroups.find(
    (g) => g.label === FilterItemLabel.PriceRange
  );
  const item = priceFilter?.items[0];

  if (!item?.selected) {
    return null;
  }

  return {
    minPrice: formatToNumber(item.inputMinPrice),
    maxPrice: formatToNumber(item.inputMaxPrice),
  };
}

export function formatToNumber(value: string): number {
  return parseInt(value.replace(/\,/g, ''), 10);
}


// returns true if all shipment addresses in the order item are unique, false otherwise
export function checkForUniqueShipmentAddresses(orderItem: OrderItem): boolean {
  if (isEmptyArray(orderItem?.deliveries)) {
    return true;
  }

  const seenCombinations = new Set<string>();

  for (const delivery of orderItem.deliveries) {
    const combinationKey = `${delivery.addr_id}-${delivery.sku_id}`;

    if (seenCombinations.has(combinationKey)) {
      return false; // Duplicate combination found
    }

    seenCombinations.add(combinationKey);
  }

  return true; // All combinations are unique
}
